/** 셸 시스템 컴포지션 팩토리. 씬 결합은 deps 콜백으로 늦게 주입하고 Phaser 직접 import는 피한다. */
import { Capacitor } from "@capacitor/core";
import { resolveAdPlatform } from "@yjsgames/house-ads";
import type { SaveAdapter } from "@yjsgames/save";
import type { DailyResetService } from "@yjsgames/daily-reset";
import type { CooldownService } from "@yjsgames/cooldown";
import { CheatActions } from "../systems/CheatActions.js";
import { CombatSystem } from "../systems/CombatSystem.js";
import { createRewardedAdProvider } from "../ads/createRewardedAdProvider.js";
import { BannerController, NoopBannerProvider, NoopRewardedAdProvider } from "@yjsgames/ad-ports";
import type { RewardedAdProvider } from "@yjsgames/ad-ports";
import { HOUSE_AD_MOBILE_SHARE } from "../ads/houseAdData.js";
import { createBannerAdProvider } from "../ads/createBannerAdProvider.js";
import {
  CrazyGamesBannerController,
  shouldCreateCrazyGamesBannerController,
} from "../ads/CrazyGamesBannerController.js";
import { CrazyGamesBannerProvider } from "../ads/CrazyGamesBannerProvider.js";
import { currentCrazyGamesSdk } from "../ads/CrazyGamesSdk.js";
import type { WebAdProvider } from "../ads/createRewardedAdProvider.js";
import { SettingsFlow } from "./SettingsFlow.js";
import type { TargetFpsSetting } from "@yjsgames/perf";
import { IapFlow } from "./IapFlow.js";
import { RefreshScheduler } from "./RefreshScheduler.js";
import { PersistScheduler } from "./PersistScheduler.js";
import { ChapterFlow } from "./ChapterFlow.js";
import { GuideQuestFlow } from "./GuideQuestFlow.js";
import { createGuideQuestFlow } from "./createGuideQuestFlow.js";
import type { ChapterController } from "../systems/ChapterController.js";
import { RewardService } from "./commands/RewardService.js";
import { EconomyCommands } from "./commands/economyCommands.js";
import { ProgressionCommands } from "./commands/progressionCommands.js";
import { DailyShopCommands } from "./commands/dailyShopCommands.js";
import { ShopAdCommands } from "./commands/shopAdCommands.js";
import { GridCommands } from "./commands/gridCommands.js";
import type { ShellPorts } from "./commands/ports.js";
import type { GameContext } from "./GameContext.js";
import type { LanguageCode } from "../i18n/locales.js";
import type { NumberFormat } from "../models/ModuleBrickSaveData.js";
import type { PieceRandomSource } from "../data/modulePieces.js";
import type { FeatureFlags } from "../env/featureFlags.js";
import { RewardModalPresenter } from "../ui/RewardModalPresenter.js";
import type { ModalHost } from "../ui/ModalHost.js";
import { showRewardedWithLoading } from "./showRewardedWithLoading.js";
import { APP_FLAVOR } from "../env/appFlavor.js";
import type { DialogueTrigger } from "../data/dialogueData.js";

const CG_DIAG = (import.meta.env["VITE_WEB_AD_PROVIDER"] as string | undefined) === "crazygames";

function diag(message: string, data?: Record<string, unknown>): void {
  if (!CG_DIAG) return;
  console.info(`[CG-DIAG][ShellSystems] ${message}`, data ?? {});
}

type IdleCallbackHandle = number;

interface IdleSchedulerGlobal {
  requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => IdleCallbackHandle;
  cancelIdleCallback?: (handle: IdleCallbackHandle) => void;
}

export interface ShellSystemsDeps {
  ctx: GameContext;
  features: FeatureFlags;
  modals: ModalHost;
  screen(): { width: number; height: number };
  schedule(cb: () => void, ms: number): () => void;
  adFree(): boolean;
  setAdFree(value: boolean): void;
  discovered(): Set<string>;
  setBoxResult(message: string): void;
  pieceRng(): PieceRandomSource;
  cheatForceHouseAd(): boolean;
  toggleForceHouseAd(): boolean;
  dailyReset(): DailyResetService;
  cooldown(): CooldownService;
  shopDailyAdDiamondClaimed(): boolean;
  setShopDailyAdDiamondClaimed(value: boolean): void;
  loadRawSave(): Promise<Record<string, unknown> | null>;
  invalidateUi(): void;
  /** 챕터 잠금 탭 배지 갱신 — ChapterFlow가 bestChapter 변동 시 호출(GameScene이 bottomTab.setLocked로 구현). */
  refreshTabLocks(): void;
  /** 정상 전투 진행 중 강제 다이어로그 trigger를 UI runtime으로 전달한다. */
  forceDialogueTrigger(trigger: DialogueTrigger): void;
  persist(): void;
  persistNow(): void;
  reloadApp(): void;
  toggleBannerPreview(): boolean;
  bannerPreviewEnabled(): boolean;
  /** PersistScheduler의 실제 저장 — 씬이 toSaveData 직렬화 후 saveSystem.save. */
  saveNow(): void;
  /** 저장 가능 게이트 — 로드 완료 전(canRefresh false) 저장 무시. */
  canSave(): boolean;
  /** uiRefresh flush — battleTab+활성 탭 갱신 (씬이 readiness 가드 포함 구현). */
  onUiFlush(): void;
  refreshAll(): void;
  showToast(message: string): void;
  resetFireTimer(): void;
  /** 모듈 배치 성공 알림 — 튜토리얼 place 스텝(actionKey=module.placed) 진행. 호출 시점 lazy(러너 늦게 생성). */
  notifyModulePlaced(): void;
  /** 미스터리 100% 강제 스폰 토글 — 러너 플래그 set + 현재 챕터 재빌드 (디버그 치트). */
  setForceMysteryBricks(enabled: boolean): void;
  /** 특수 벽돌(방어막/장갑/재생) 강제 스폰 토글 — 러너 플래그 set + 현재 챕터 재빌드 (QA 치트). */
  setForceSpecialBricks(enabled: boolean): void;
  /** 강제 크래시 — Crashlytics 결선 검증(recordException 비치명). 씬이 registry "crashlytics"로 결선. */
  forceCrash(): void;
  /** 챕터 패배 전환 연출 강제 실행 — 설정 치트 패널 QA용. */
  triggerChapterDefeat(): void;
  /** 환생 전환 연출 미리보기 — 저장/성장 상태 변경 없이 설정 치트 패널에서 확인. */
  previewRebirthTransition(): void;
  /** 앱 시작 후 경과 시간 오버레이 토글 — 새 상태(true=ON) 반환. 세션 한정. */
  toggleAppTimerOverlay(): boolean;
  /** 앱 타이머 오버레이 현재 상태. */
  appTimerOverlayEnabled(): boolean;
  resetBattlefield(): void;
  applySpaceshipVisualScale?(): void;
  /** 챕터 전환 연출 — 게임뷰 오버레이 + 새 벽돌 페이드인 (러너). */
  transitionChapterGrid(): void;
  /** ChapterController 교체 — 셸이 controllers 번들 스왑으로 반영 (치트 점프). */
  replaceChapter(next: ChapterController): void;
  /** 언어 변경 뷰 전파(미러/재번역/HUD) — SettingsFlow 콜백 (씬 구현). */
  onLanguageApplied(code: LanguageCode, dirChanged: boolean): void;
  onNumberFormatApplied(format: NumberFormat): void;
  /** 목표 FPS 변경 전역 반영 — 씬이 game.loop 캡 조정 + auto면 adaptive 재시작. */
  onTargetFpsApplied(setting: TargetFpsSetting): void;
  /** 현재 활성 탭 (CrazyGames 배너 surface 판정용). */
  activeTab(): import("../ui/tabCatalog.js").TabId;
  /** 설정 모달 열림 여부 (CrazyGames 배너 surface 판정용). */
  settingsOpen(): boolean;
  /** CrazyGames 웹 배너 노출 중 전투/타이머 진행 정지. */
  pauseGameplayForAd(): void;
  /** CrazyGames 웹 배너 제거 후 전투/타이머 진행 재개. */
  resumeGameplayForAd(): void;
}

export interface ShellSystems {
  /** 챕터 전진/점프·던전 보상 지급 흐름 (W5). */
  chapter: ChapterFlow;
  /** 치트 액션 — 챕터·재화·광고 강제·저장 단축키. */
  cheats: CheatActions;
  /** 설정 흐름 — 언어·숫자 표기·볼륨·계정삭제 (S4c). */
  settings: SettingsFlow;
  /** 전투 결정 로직 — 발사 간격·데미지·치명타 계산 (G1). */
  combat: CombatSystem;
  /** 보상형 광고 Provider (DIP) — 웹=하우스, 네이티브=LevelPlay, 비활성=Noop. */
  rewardedAd: RewardedAdProvider;
  /** 모바일 상단 배너 컨트롤러 (DIP) — 부팅 evaluate + 광고제거 전환 시 재평가. */
  bannerController: BannerController;
  /** CrazyGames 웹 배너 컨트롤러 — 웹+crazygames일 때만 존재, 탭/설정 전환 시 evaluate. */
  crazyGamesBannerController: CrazyGamesBannerController | null;
  /** 결제 흐름 — 구매·entitlement 동기화·adFree 설정 (S5). */
  iap: IapFlow;
  /** 커맨드 부수효과 Port — invalidate/persist/resetField 공용 허브 (S2). */
  ports: ShellPorts;
  /** 통합 보상 지급 서비스 — 골드/다이아/모듈/환생포인트 등 ResolvedReward 처리. */
  rewards: RewardService;
  /** 데이터 주도 가이드퀘스트 이벤트/보상 수령 흐름. */
  guideQuest: GuideQuestFlow;
  /** 경제 커맨드 — 상점 구매·골드 상자 오픈·아이템 소비. */
  economy: EconomyCommands;
  /** 진행 커맨드 — 환생·유물·ChapterFlow 라우팅. */
  progression: ProgressionCommands;
  /** 보상 획득 모달 표시기 — ResolvedReward를 팝업으로 렌더. */
  rewardPresenter: RewardModalPresenter;
  /** 일일상점 커맨드 — 구매·리롤·광고 시청 연동. */
  dailyShop: DailyShopCommands;
  /** 상점 광고 다이아 커맨드 — 2시간 쿨타임/오늘의 광고 다이아 구매. */
  shopAd: ShopAdCommands;
  /** 모듈 그리드 커맨드 — 모듈 부여·배치·해금 처리. */
  grid: GridCommands;
  /** UI 갱신 배처 — 50ms 윈도로 다발 invalidate 합산 후 1회 flush (G3). */
  uiRefresh: RefreshScheduler;
  /** 저장 배처 — 800ms 윈도로 persist 다발을 1회 직렬화·쓰기로 합산 (G4/S4a). */
  persistScheduler: PersistScheduler;
}

export interface ShellSystemsEnv {
  saveAdapter?: SaveAdapter;
  settingsAdapter?: SaveAdapter;
}

export function createShellSystems(deps: ShellSystemsDeps): ShellSystems {
  const { ctx } = deps;

  const chapter = new ChapterFlow({
    ctx,
    grantModule: (id) => void grid.grantModule(id),
    grantReward: (reward) => {
      const insts = rewards.grant(reward);
      rewardPresenter.open(reward, insts);
    },
    discovered: deps.discovered,
    transitionChapterGrid: deps.transitionChapterGrid,
    rebuildField: deps.resetBattlefield,
    replaceChapter: deps.replaceChapter,
    invalidateUi: deps.invalidateUi,
    refreshTabLocks: deps.refreshTabLocks,
    forceDialogueTrigger: deps.forceDialogueTrigger,
    onChapterReached: (chapter) => guideQuest.notifyChapterReached(chapter),
    persist: deps.persist,
  });

  const cheats = new CheatActions({
    economy: () => ctx.economy(),
    rebirth: () => ctx.rebirth(),
    relic: () => ctx.relic(),
    dungeon: () => ctx.dungeon(),
    dailyReset: () => deps.dailyReset(),
    resetAdGates: () => shopAd.resetGates(),
    setChapter: (n) => chapter.setChapter(n),
    unlockAllTabs: () => chapter.unlockAllTabs(),
    spaceship: () => ctx.spaceship(),
    resetFireTimer: () => deps.resetFireTimer(),
    refreshAll: () => deps.refreshAll(),
    persist: () => deps.persist(),
    toggleForceHouseAd: () => deps.toggleForceHouseAd(),
    setForceMysteryBricks: (e) => deps.setForceMysteryBricks(e),
    setForceSpecialBricks: (e) => deps.setForceSpecialBricks(e),
    grid: () => ctx.grid(),
    inventory: () => ctx.inventory(),
    idSource: () => ctx.idSource(),
    pieceRng: () => deps.pieceRng(),
    toggleBannerPreview: () => deps.toggleBannerPreview(),
    bannerPreviewEnabled: () => deps.bannerPreviewEnabled(),
    clearAdFree: () => {
      deps.setAdFree(false);
      deps.persistNow();
      bannerController.evaluate();
      crazyGamesBannerController?.evaluate();
      deps.reloadApp();
    },
    forceCrash: () => deps.forceCrash(),
    triggerChapterDefeat: () => deps.triggerChapterDefeat(),
    previewRebirthTransition: () => deps.previewRebirthTransition(),
    toggleAppTimerOverlay: () => deps.toggleAppTimerOverlay(),
    appTimerOverlayEnabled: () => deps.appTimerOverlayEnabled(),
  });

  const settings = new SettingsFlow({
    store: () => ctx.settingsStore(),
    soundController: () => ctx.soundController(),
    loadRawSave: () => deps.loadRawSave(),
    onLanguageApplied: deps.onLanguageApplied,
    onNumberFormatApplied: deps.onNumberFormatApplied,
    onTargetFpsApplied: deps.onTargetFpsApplied,
  });

  const combat = new CombatSystem({
    grid: () => ctx.grid(),
    inventory: () => ctx.inventory(),
    playerModuleLevels: () => ctx.playerModuleLevels(),
    moduleMastery: () => ctx.moduleMastery(),
    synergyLevels: () => ctx.synergyLevels(),
    bestChapter: () => ctx.chapter().bestChapter,
    spaceship: () => ctx.spaceship(),
    moduleRegistry: () => ctx.moduleRegistry(),
    specBuilder: () => ctx.specBuilder(),
    relic: () => ctx.relic(),
    statUpgrade: () => ctx.statUpgrade(),
    rebirthUpgrade: () => ctx.rebirthUpgrade(),
  });

  const webAdProvider: WebAdProvider =
    ((import.meta.env["VITE_WEB_AD_PROVIDER"] as string | undefined) === "crazygames"
      ? "crazygames"
      : "house") as WebAdProvider;
  const useLevelPlayTestAds = APP_FLAVOR !== "live";
  diag("create", {
    appFlavor: APP_FLAVOR,
    webAdProvider,
    features: deps.features,
    adFree: deps.adFree(),
    isNative: Capacitor.isNativePlatform(),
  });

  const rewardedAd: RewardedAdProvider = deps.features.houseAds
    ? createRewardedAdProvider({
        analytics: ctx.analytics(),
        openModal: (req) => deps.modals.openHouseAd(req),
        closeModal: () => deps.modals.closeHouseAd(),
        appKey:
          (Capacitor.getPlatform() === "ios"
            ? (import.meta.env["VITE_LEVELPLAY_APP_KEY_IOS"] as string | undefined)
            : (import.meta.env["VITE_LEVELPLAY_APP_KEY_ANDROID"] as string | undefined)) ?? "",
        placement:
          (import.meta.env["VITE_LEVELPLAY_REWARDED_PLACEMENT"] as string | undefined) ??
          "DefaultRewardedVideo",
        // 웹은 기본 Play, 단 모바일 웹이면 userAgent로 OS 감지해 해당 스토어로 (네이티브는 그대로).
        platform: resolveAdPlatform(Capacitor.getPlatform(), navigator.userAgent),
        mobileShare: HOUSE_AD_MOBILE_SHARE,
        testMode: useLevelPlayTestAds,
        forceHouse: () => deps.cheatForceHouseAd(),
        webAdProvider,
      })
    : new NoopRewardedAdProvider();

  const bannerProvider = deps.features.banner
    ? createBannerAdProvider({
        levelPlay: {
          appKey:
            (Capacitor.getPlatform() === "ios"
              ? (import.meta.env["VITE_LEVELPLAY_APP_KEY_IOS"] as string | undefined)
              : (import.meta.env["VITE_LEVELPLAY_APP_KEY_ANDROID"] as string | undefined)) ?? "",
          adUnitId:
            (Capacitor.getPlatform() === "ios"
              ? (import.meta.env["VITE_LEVELPLAY_BANNER_AD_UNIT_ID_IOS"] as string | undefined)
              : (import.meta.env["VITE_LEVELPLAY_BANNER_AD_UNIT_ID_ANDROID"] as string | undefined)) ??
            "",
          placement:
            (import.meta.env["VITE_LEVELPLAY_BANNER_PLACEMENT"] as string | undefined) ??
            "TopBanner",
          testMode: useLevelPlayTestAds,
        },
      })
    : new NoopBannerProvider();
  const bannerController = new BannerController({
    provider: bannerProvider,
    isNative: Capacitor.isNativePlatform(),
    featureEnabled: () => deps.features.banner,
    adFree: deps.adFree,
  });

  const crazyGamesBannerController =
    shouldCreateCrazyGamesBannerController({
      isNative: Capacitor.isNativePlatform(),
      webAdProvider,
      isDev: import.meta.env.DEV,
    })
      ? new CrazyGamesBannerController({
          provider: new CrazyGamesBannerProvider("crazygames-banner", 728, 90, {
            devFallback: import.meta.env.DEV,
          }),
          getInput: () => ({
            activeTab: deps.activeTab(),
            settingsOpen: deps.settingsOpen(),
            adFree: deps.adFree(),
            enabled: deps.features.banner,
          }),
          gameplayStop: () => currentCrazyGamesSdk()?.game.gameplayStop(),
          gameplayStart: () => currentCrazyGamesSdk()?.game.gameplayStart(),
          pauseGameplay: () => deps.pauseGameplayForAd(),
          resumeGameplay: () => deps.resumeGameplayForAd(),
        })
      : null;
  diag("crazyGamesBannerController", {
    created: Boolean(crazyGamesBannerController),
    webAdProvider,
    bannerEnabled: deps.features.banner,
  });

  const iap = new IapFlow({
    analytics: () => ctx.analytics(),
    analyticsMilestones: () => ctx.analyticsMilestones(),
    economy: () => ctx.economy(),
    adFree: deps.adFree,
    setAdFree: deps.setAdFree,
    setBoxResult: deps.setBoxResult,
    invalidateUi: deps.invalidateUi,
    persistNow: deps.persistNow,
    onStoreProcessingStart: () => deps.modals.openStoreLoading(),
    onStoreProcessingEnd: () => deps.modals.closeStoreLoading(),
    // 광고제거 구매/복원 시 배너 제거 + 전체화면 재적용(reload). bannerController는 바로 위에서 생성.
    onAdFreeChanged: () => {
      bannerController.evaluate();
      crazyGamesBannerController?.evaluate();
      deps.reloadApp();
    },
  });

  const ports: ShellPorts = {
    setBoxResult: deps.setBoxResult,
    invalidateUi: deps.invalidateUi,
    persist: deps.persist,
    persistNow: deps.persistNow,
    resetFireTimer: deps.resetFireTimer,
    resetBattlefield: deps.resetBattlefield,
    applySpaceshipVisualScale: deps.applySpaceshipVisualScale,
  };

  const rewardPresenter = new RewardModalPresenter({ modals: deps.modals, screen: deps.screen });
  let rewardedAdProcessing = false;
  const isAdProcessing = () => rewardedAdProcessing;
  const setRewardedAdProcessing = (processing: boolean): void => {
    if (rewardedAdProcessing !== processing) {
      rewardedAdProcessing = processing;
      deps.invalidateUi();
    }
  };
  const showRewardedAdWithLoading = (onReward: () => void): void =>
    showRewardedWithLoading(rewardedAd, deps.modals, onReward, {
      isProcessing: isAdProcessing,
      setProcessing: setRewardedAdProcessing,
    });

  const rewards = new RewardService({
    ctx,
    grantModule: (id, rarity) => grid.grantModule(id, undefined, rarity),
  });
  const guideQuest = createGuideQuestFlow({ ctx, ports, rewards, rewardPresenter });
  const economy = new EconomyCommands({
    ctx,
    ports,
    rewards,
    discovered: deps.discovered,
    openRewardModal: (r, insts) => rewardPresenter.open(r, insts),
    onActionCompleted: (target) => guideQuest.notifyActionCompleted(target),
  });
  const progression = new ProgressionCommands({
    ctx,
    ports,
    combatProbe: () =>
      `baseDmg=${combat.baseDamage().toString()} fireMs=${combat.fireIntervalMs().toFixed(0)}`,
    onUpgradeBought: (target) => guideQuest.notifyUpgradeBought(target),
    onActionCompleted: (target) => guideQuest.notifyActionCompleted(target),
  });
  const dailyShop = new DailyShopCommands({
    ctx,
    ports,
    rewards,
    adFree: deps.adFree,
    adProcessing: isAdProcessing,
    showRewardedAd: showRewardedAdWithLoading,
    showToast: deps.showToast,
    openRewardModal: (r, insts) => rewardPresenter.open(r, insts),
    onActionCompleted: (target) => guideQuest.notifyActionCompleted(target),
  });
  const shopAd = new ShopAdCommands({
    ctx,
    ports,
    rewards,
    cooldown: deps.cooldown,
    adFree: deps.adFree,
    adProcessing: isAdProcessing,
    dailyClaimed: deps.shopDailyAdDiamondClaimed,
    setDailyClaimed: deps.setShopDailyAdDiamondClaimed,
    showRewardedAd: showRewardedAdWithLoading,
    showToast: deps.showToast,
    openRewardModal: (r, insts) => rewardPresenter.open(r, insts),
  });
  const grid = new GridCommands({
    ctx,
    ports,
    discovered: deps.discovered,
    pieceRng: deps.pieceRng,
    snapbackBattleGrid: deps.onUiFlush, // 드래그 스냅백 — battle 즉시 재구성 (flush 경로 공용)
    onPlaced: (definitionId) => {
      deps.notifyModulePlaced();
      guideQuest.notifyModulePlaced(definitionId);
    },
  });

  // G3 선택 갱신 배처 — 50ms 윈도, flush 시 battleTab+활성 탭만 (씬 onUiFlush가 readiness 가드).
  const uiRefresh = new RefreshScheduler({ schedule: deps.schedule });
  uiRefresh.flushed.subscribe(() => deps.onUiFlush());

  // G4/S4a — persist 다발을 800ms 윈도로 합쳐 직렬화+쓰기 1회.
  const idleGlobal = globalThis as unknown as IdleSchedulerGlobal;
  const persistScheduler = new PersistScheduler({
    save: deps.saveNow,
    schedule: deps.schedule,
    deferSave: (cb) => {
      if (typeof idleGlobal.requestIdleCallback === "function") {
        const handle = idleGlobal.requestIdleCallback(cb, { timeout: 1500 });
        return () => idleGlobal.cancelIdleCallback?.(handle);
      }
      return deps.schedule(cb, 0);
    },
    canSave: deps.canSave,
  });

  return {
    chapter,
    cheats,
    settings,
    combat,
    rewardedAd,
    bannerController,
    crazyGamesBannerController,
    iap,
    ports,
    rewards,
    guideQuest,
    economy,
    progression,
    rewardPresenter,
    dailyShop,
    shopAd,
    grid,
    uiRefresh,
    persistScheduler,
  };
}
